import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { authApi } from '../api/auth';
import { BridgeMark } from '../components/icons/Svg';
import { Button } from '../ui/Button';
import { Input, Label } from '../ui/Input';
import { cn } from '../lib/cn';

export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  const pwOk = pw.length >= 8;
  const matchOk = pw && pw === pw2;

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!pwOk) {
      setErr('Password must be at least 8 characters.');
      return;
    }
    if (!matchOk) {
      setErr('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await authApi.resetPassword({ token, new_password: pw });
      setDone(true);
      setTimeout(() => nav('/login'), 1500);
    } catch (ex) {
      setErr(
        ex?.response?.data?.detail ||
          'This reset link is invalid or expired. Request a new one.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-[hsl(var(--bg))] px-4 text-[hsl(var(--fg))]">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 -z-0 h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl animate-brief-breathe"
        style={{
          background:
            'radial-gradient(closest-side, hsl(var(--primary) / 0.14), transparent 70%)',
        }}
      />
      <form
        className={cn(
          'relative z-10 flex w-full max-w-md flex-col gap-5',
          'rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-8 shadow-elev-3'
        )}
        onSubmit={submit}
      >
        <div className="flex items-center gap-3">
          <BridgeMark size={36} />
          <h1 className="m-0 text-xl font-semibold tracking-tight">Set a new password</h1>
        </div>

        {done ? (
          <>
            <p className="text-sm text-[hsl(var(--success))]">
              Password updated. Redirecting you to sign in.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-[hsl(var(--fg-muted))]">
              Choose a new password. Minimum 8 characters.
            </p>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rp-pw">New password</Label>
              <Input
                id="rp-pw"
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                autoFocus
                required
                minLength={8}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rp-pw2">Confirm new password</Label>
              <Input
                id="rp-pw2"
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                required
                minLength={8}
              />
              {pw2 && !matchOk && (
                <span className="text-[11px] text-[hsl(var(--danger))]">
                  Passwords do not match
                </span>
              )}
            </div>
            {err && (
              <div
                role="alert"
                className="rounded-lg border border-[hsl(var(--danger)/0.35)] bg-[hsl(var(--danger)/0.1)] px-3 py-2 text-sm"
              >
                {err}
              </div>
            )}
            <Button
              type="submit"
              disabled={busy || !pwOk || !matchOk}
              className="w-full"
            >
              {busy ? 'Saving' : 'Set new password'}
            </Button>
            <Link
              to="/login"
              className="text-center text-xs text-[hsl(var(--fg-muted))] hover:text-[hsl(var(--fg))]"
            >
              Back to sign in
            </Link>
          </>
        )}
      </form>
    </div>
  );
}
